import { useMemo, useState } from 'react';
import { numberText, wholeNumber } from '../../lib/wholeNumber.mjs';
import { pickerChoices } from '../../lib/pickerChoices.mjs';

// Generic field grid for the Bids & Specs forms. A field is
// { key, label, type: text|textarea|date|number|money|select|bool|tri|picker, options?, span?, hint? }.
// Values stay in API shape (money in cents, bools as true/false/null).

export const inputCls = 'w-full rounded-lg border border-[var(--border)] bg-white px-2.5 py-1.5 text-sm text-[var(--text-primary)] dark:bg-slate-800';

export const usd = (c) => (c == null ? '' : `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
export function toCents(v) {
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(v ?? '').replace(/[$,\s]/g, ''));
  return m ? Number(m[1]) * 100 + Number((m[2] || '0').padEnd(2, '0')) : null;
}
export const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function Picker({ value, onChange, options, placeholder }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  const shown = useMemo(() => pickerChoices(options, q), [q, options]);
  return (
    <div className="relative">
      <input className={inputCls} placeholder={placeholder || 'Search'} value={open ? q : (current?.label || '')}
        onFocus={() => { setOpen(true); setQ(''); }} onBlur={() => setTimeout(() => setOpen(false), 150)} onChange={(e) => setQ(e.target.value)} />
      {open && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-[var(--border)] bg-white shadow-lg dark:bg-slate-800">
          {value && <button type="button" className="block w-full px-3 py-1.5 text-left text-xs text-rose-600" onMouseDown={() => onChange(null)}>Clear</button>}
          {shown.map((o) => (
            <button type="button" key={o.value} className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-slate-700 ${o.value === value ? 'font-semibold' : ''}`}
              onMouseDown={() => { onChange(o.value); setOpen(false); }}>{o.label}</button>
          ))}
          {!shown.length && <div className="px-3 py-2 text-xs text-[var(--text-secondary)]">No match</div>}
        </div>
      )}
    </div>
  );
}

export function FieldGrid({ fields, value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {fields.map((f) => {
        const v = value[f.key];
        let control;
        switch (f.type) {
          case 'textarea':
            control = <textarea className={inputCls} rows={f.rows || 3} value={v ?? ''} onChange={(e) => set(f.key, e.target.value)} />;
            break;
          case 'date':
            control = <input type="date" className={inputCls} value={v ?? ''} onChange={(e) => set(f.key, e.target.value || null)} />;
            break;
          case 'number':
            control = <input inputMode="numeric" className={inputCls} value={v ?? ''} onChange={(e) => set(f.key, numberText(e.target.value))} />;
            break;
          case 'money':
            control = <input inputMode="decimal" className={inputCls} placeholder="0.00" value={v ?? ''} onChange={(e) => set(f.key, e.target.value)} />;
            break;
          case 'select':
            control = (
              <select className={inputCls} value={v ?? ''} onChange={(e) => set(f.key, e.target.value || null)}>
                <option value="">{f.empty || 'Not set'}</option>
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            );
            break;
          case 'tri':
            control = (
              <select className={inputCls} value={v == null ? '' : String(v)} onChange={(e) => set(f.key, e.target.value === '' ? null : e.target.value === 'true')}>
                <option value="">Unknown</option><option value="true">Yes</option><option value="false">No</option>
              </select>
            );
            break;
          case 'bool':
            control = (
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!v} onChange={(e) => set(f.key, e.target.checked)} /> {f.checkLabel || 'Yes'}</label>
            );
            break;
          case 'picker':
            control = <Picker value={v ?? null} onChange={(x) => set(f.key, x)} options={f.options} placeholder={f.placeholder} />;
            break;
          default:
            control = <input className={inputCls} value={v ?? ''} onChange={(e) => set(f.key, e.target.value)} />;
        }
        return (
          <label key={f.key} className={`block text-sm ${f.span === 2 || f.type === 'textarea' ? 'sm:col-span-2' : ''}`}>
            <span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">{f.label}{f.required ? ' *' : ''}</span>
            {control}
            {f.hint && <span className="mt-0.5 block text-xs text-[var(--text-secondary)]">{f.hint}</span>}
          </label>
        );
      })}
    </div>
  );
}

// Convert form state to an API payload. Money fields hold typed text until saved.
// Returns { payload, error }. With `original`, only changed keys are sent (PATCH).
export function toPayload(fields, value, original = null) {
  const out = {};
  for (const f of fields) {
    let v = value[f.key];
    if (f.type === 'money') {
      if (v === '' || v == null) v = null;
      else if (typeof v === 'string') {
        const c = toCents(v);
        if (c == null) return { error: `${f.label}: enter a dollar amount` };
        v = c;
      }
    } else if (f.type === 'number') {
      if (v === '' || v == null) v = null;
      else {
        const n = wholeNumber(v);
        if (n == null) return { error: `${f.label}: enter a whole number` };
        v = n;
      }
    } else if (typeof v === 'string') {
      v = v.trim() === '' ? null : v;
    }
    if (f.required && (v == null || v === '')) return { error: `${f.label} is required` };
    if (original) {
      const o = original[f.key] ?? null;
      if (JSON.stringify(o) === JSON.stringify(v ?? null)) continue;
    } else if (v == null && !f.required) continue;
    out[f.key] = v;
  }
  return { payload: out };
}

// Form state from a record: money fields become editable dollar text.
export function toFormState(fields, record = {}) {
  const s = {};
  for (const f of fields) {
    const v = record[f.key];
    s[f.key] = f.type === 'money' ? (v == null ? '' : (v / 100).toFixed(2)) : (v ?? (f.type === 'bool' ? false : null));
  }
  return s;
}

export function Chip({ children, tone = 'gray' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-200',
    green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    red: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
    blue: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
    indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  };
  return <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone] || tones.gray}`}>{children}</span>;
}

// Authenticated file download (the API helper only speaks JSON).
export async function downloadFile(path, { method = 'GET', body, fallbackName = 'download' } = {}) {
  const res = await fetch(`/api${path}`, {
    method,
    headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) { localStorage.removeItem('mickey_token'); window.location.href = '/login'; return; }
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="([^"]+)"/.exec(cd);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// Fetch an authenticated HTML/PDF and open it in a new tab (for printing).
export async function openFile(path, { method = 'GET' } = {}) {
  const win = window.open('', '_blank');
  const res = await fetch(`/api${path}`, { method, headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}` } });
  // An expired session goes to sign-in, as downloadFile and the api helper do.
  if (res.status === 401) { if (win) win.close(); localStorage.removeItem('mickey_token'); window.location.href = '/login'; return; }
  if (!res.ok) { if (win) win.close(); const j = await res.json().catch(() => ({})); throw new Error(j.error || `Failed (${res.status})`); }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  if (win) win.location.href = url; else window.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function uploadFile(path, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`/api${path}`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}` }, body: fd });
  // An expired session goes to sign-in, as downloadFile and the api helper do.
  if (res.status === 401) { localStorage.removeItem('mickey_token'); window.location.href = '/login'; return; }
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `Upload failed (${res.status})`);
  return j;
}
